import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  UserCircle, EnvelopeSimple, LockKey, Sparkle, Users, SignOut, Receipt,
  CheckCircle, Crown, UserPlus, Trash, ArrowRight,
} from "@phosphor-icons/react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const Section = ({ icon: Icon, title, subtitle, children, testid }) => (
  <motion.section
    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
    className="rounded-3xl border border-sand-300 bg-white p-6 sm:p-8 shadow-ambient"
    data-testid={testid}
  >
    <div className="flex items-start gap-4 mb-6">
      <div className="h-11 w-11 rounded-2xl bg-terracotta-50 text-terracotta-600 grid place-items-center shrink-0">
        <Icon weight="duotone" size={22} />
      </div>
      <div>
        <h2 className="font-heading text-xl sm:text-2xl text-stoneink-900 font-medium tracking-tight">{title}</h2>
        {subtitle && <p className="text-sm text-stoneink-500 mt-1">{subtitle}</p>}
      </div>
    </div>
    {children}
  </motion.section>
);

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">{label}</label>
    <div className="mt-2">{children}</div>
  </div>
);

const tierLabel = (t) => t === "family" ? "Family" : t === "pro" ? "Pro" : "Free";
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return iso; }
};

export default function Profile() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [emailPassword, setEmailPassword] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const [sub, setSub] = useState(null);
  const [household, setHousehold] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    if (user) { setName(user.name || ""); setEmail(user.email || ""); }
  }, [user]);

  const loadSidebars = async () => {
    try {
      const [s, h] = await Promise.all([api.get("/auth/subscription"), api.get("/household")]);
      setSub(s.data); setHousehold(h.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadSidebars(); }, []);

  const saveProfile = async (e) => {
    e.preventDefault();
    const emailChanged = email.toLowerCase() !== (user?.email || "").toLowerCase();
    if (emailChanged && !emailPassword) {
      toast.error("Enter your current password to change email"); return;
    }
    setSavingProfile(true);
    try {
      const body = { name };
      if (emailChanged) { body.email = email; body.current_password = emailPassword; }
      const { data } = await api.patch("/auth/profile", body);
      if (data.updated) toast.success("Profile updated");
      else toast.message("Nothing to update");
      setEmailPassword("");
      await refresh();
    } catch (err) { toast.error(formatError(err)); } finally { setSavingProfile(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) { toast.error("Passwords don't match"); return; }
    if (newPw.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: curPw, new_password: newPw });
      toast.success("Password updated");
      setCurPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) { toast.error(formatError(err)); } finally { setSavingPw(false); }
  };

  const invite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      await api.post("/household/invite", { email: inviteEmail });
      toast.success(`Invited ${inviteEmail}`);
      setInviteEmail(""); loadSidebars();
    } catch (err) { toast.error(formatError(err)); } finally { setInviting(false); }
  };

  const removeMember = async (id) => {
    if (!confirm("Remove this member?")) return;
    try { await api.post(`/household/remove/${id}`); loadSidebars(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const leaveHousehold = async () => {
    if (!confirm("Leave this household? You'll lose family-plan access.")) return;
    try { await api.post("/household/leave"); await refresh(); loadSidebars(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const doLogout = async () => { await logout(); navigate("/login"); };

  const tier = user?.tier || "free";
  const isPro = tier === "pro" || tier === "family";
  const tierSource = user?.tier_source;
  // User is a "family owner" capable of inviting if they have family tier from self/admin
  // (household DB row gets created lazily on first invite — don't gate UI on it).
  const canInvite = tier === "family" && (tierSource === "self" || tierSource === "admin");
  const isFamilyOwner = canInvite;
  const isFamilyMember = household?.role === "member";

  return (
    <div className="space-y-8 max-w-3xl" data-testid="profile-page">
      <div>
        <h1 className="font-heading text-4xl sm:text-5xl text-stoneink-900 font-semibold tracking-tight">Your account</h1>
        <p className="mt-2 text-stoneink-700">Settings, subscription, and family — all in one place.</p>
      </div>

      {/* Personal info */}
      <Section icon={UserCircle} title="Personal info" subtitle="Update your name or email." testid="section-personal">
        <form onSubmit={saveProfile} className="space-y-5">
          <Field label="Name">
            <input
              data-testid="profile-name-input"
              value={name} onChange={(e) => setName(e.target.value)} required
              className="w-full h-11 px-4 rounded-xl bg-white border border-sand-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-500/20 outline-none"
            />
          </Field>
          <Field label="Email">
            <input
              data-testid="profile-email-input"
              type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="w-full h-11 px-4 rounded-xl bg-white border border-sand-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-500/20 outline-none"
            />
          </Field>
          {email.toLowerCase() !== (user?.email || "").toLowerCase() && (
            <Field label="Confirm current password">
              <input
                data-testid="profile-email-password-input"
                type="password" value={emailPassword} onChange={(e) => setEmailPassword(e.target.value)} required
                placeholder="Required to change email"
                className="w-full h-11 px-4 rounded-xl bg-white border border-sand-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-500/20 outline-none"
              />
            </Field>
          )}
          <div className="flex justify-end">
            <button
              data-testid="profile-save-btn" type="submit" disabled={savingProfile}
              className="h-11 px-5 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium disabled:opacity-60 active:scale-[0.98]"
            >
              {savingProfile ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </Section>

      {/* Password */}
      <Section icon={LockKey} title="Password" subtitle="Use a strong, unique passphrase." testid="section-password">
        <form onSubmit={changePassword} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Current">
            <input
              data-testid="profile-current-password" type="password" value={curPw}
              onChange={(e) => setCurPw(e.target.value)} required
              className="w-full h-11 px-4 rounded-xl bg-white border border-sand-300 focus:border-terracotta-500 outline-none"
            />
          </Field>
          <Field label="New">
            <input
              data-testid="profile-new-password" type="password" value={newPw}
              onChange={(e) => setNewPw(e.target.value)} required minLength={6}
              className="w-full h-11 px-4 rounded-xl bg-white border border-sand-300 focus:border-terracotta-500 outline-none"
            />
          </Field>
          <Field label="Confirm">
            <input
              data-testid="profile-confirm-password" type="password" value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)} required minLength={6}
              className="w-full h-11 px-4 rounded-xl bg-white border border-sand-300 focus:border-terracotta-500 outline-none"
            />
          </Field>
          <div className="sm:col-span-3 flex justify-end">
            <button
              data-testid="profile-change-password-btn" type="submit" disabled={savingPw}
              className="h-11 px-5 rounded-xl bg-stoneink-900 hover:bg-stoneink-700 text-white font-medium disabled:opacity-60"
            >
              {savingPw ? "Updating…" : "Update password"}
            </button>
          </div>
        </form>
      </Section>

      {/* Subscription */}
      <Section icon={Sparkle} title="Subscription" subtitle="Your plan and billing history." testid="section-subscription">
        <div className="rounded-2xl border border-sand-300 bg-sand-100 p-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-0.5 rounded-full text-xs font-bold uppercase tracking-[0.16em] ${
                tier === "family" ? "bg-ochre-400/30 text-ochre-600" :
                tier === "pro" ? "bg-terracotta-500 text-white" :
                "bg-sand-200 text-stoneink-700"
              }`}>{tierLabel(tier)}</span>
              {sub?.tier_source === "household" && <span className="text-xs text-stoneink-500">via household</span>}
              {sub?.tier_source === "admin" && <span className="text-xs text-stoneink-500">admin access</span>}
              {sub?.subscription_cancelled && <span className="text-xs px-2 py-0.5 rounded-full bg-terracotta-50 text-terracotta-600 font-medium">cancelled</span>}
            </div>
            <div className="mt-2 text-stoneink-700">
              {isPro && sub?.subscription_cancelled ? (
                <>Active until <span className="font-medium text-stoneink-900">{fmtDate(sub?.subscription_end)}</span>, then reverts to Free.</>
              ) : isPro && sub?.subscription_end ? (
                <>Renews on <span className="font-medium text-stoneink-900">{fmtDate(sub?.subscription_end)}</span></>
              ) : tier === "family" && sub?.tier_source === "admin" ? "Lifetime admin access" : "Free plan"}
            </div>
          </div>
          {tier === "free" ? (
            <Link data-testid="profile-upgrade-btn" to="/pricing"
              className="h-11 px-5 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium flex items-center gap-2 active:scale-[0.98]">
              <Sparkle weight="fill" size={16} /> Upgrade
            </Link>
          ) : (
            <div className="flex items-center gap-2">
              {sub?.tier_source === "self" && (
                sub?.subscription_cancelled ? (
                  <button
                    data-testid="profile-resume-btn"
                    onClick={async () => {
                      try { await api.post("/billing/resume"); toast.success("Subscription resumed"); loadSidebars(); }
                      catch (e) { toast.error(formatError(e)); }
                    }}
                    className="h-11 px-4 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium text-sm">
                    Resume
                  </button>
                ) : (
                  <button
                    data-testid="profile-cancel-btn"
                    onClick={async () => {
                      const when = sub?.subscription_end ? new Date(sub.subscription_end).toLocaleDateString() : "the end of your billing period";
                      if (!confirm(`Cancel your ${tierLabel(tier)} subscription? You'll keep access until ${when}, then drop to Free.`)) return;
                      try { await api.post("/billing/cancel"); toast.success("Subscription cancelled. You keep access until it expires."); loadSidebars(); }
                      catch (e) { toast.error(formatError(e)); }
                    }}
                    className="h-11 px-4 rounded-xl bg-white border border-sand-300 hover:border-destructive hover:text-destructive text-stoneink-700 text-sm font-medium">
                    Cancel
                  </button>
                )
              )}
              <Link data-testid="profile-manage-plan-btn" to="/pricing"
                className="h-11 px-4 rounded-xl bg-stoneink-900 hover:bg-stoneink-700 text-white font-medium text-sm flex items-center gap-1">
                Change plan
              </Link>
            </div>
          )}
        </div>

        {sub?.history?.length > 0 && (
          <div className="mt-6">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-stoneink-500 mb-3 flex items-center gap-2">
              <Receipt size={14} /> Billing history
            </div>
            <div className="space-y-2">
              {sub.history.map((h) => (
                <div key={h.session_id} data-testid={`tx-${h.session_id}`}
                  className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 rounded-xl border border-sand-300 bg-white">
                  <div className="flex items-center gap-3">
                    <span className={`h-8 w-8 rounded-full grid place-items-center ${
                      h.payment_status === "paid" ? "bg-sage-400/20 text-sage-600" : "bg-sand-200 text-stoneink-500"
                    }`}>
                      <CheckCircle weight="fill" size={16} />
                    </span>
                    <div>
                      <div className="text-sm font-medium text-stoneink-900">{tierLabel(h.plan)} plan</div>
                      <div className="text-xs text-stoneink-500">{fmtDate(h.created_at)}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-stoneink-900">${h.amount?.toFixed(2)} {h.currency?.toUpperCase()}</div>
                    <div className="text-xs text-stoneink-500 capitalize">{h.payment_status || "pending"}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Section>

      {/* Family / Household */}
      <Section icon={Users} title="Family members" subtitle={
        isFamilyOwner ? `Share your inventory with up to ${household?.max_members || 5} family members.` :
        isFamilyMember ? "You're a member of someone else's household." :
        "Family plan unlocks shared inventory with up to 5 people."
      } testid="section-household">
        {isFamilyOwner ? (() => {
          const members = household?.members || [];
          const maxMembers = household?.max_members || 5;
          const atLimit = members.length >= maxMembers;
          return (
          <div className="space-y-5">
            <form onSubmit={invite} className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
              <input
                data-testid="profile-invite-input"
                type="email" required value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="partner@home.app"
                className="h-11 px-4 rounded-xl bg-white border border-sand-300 focus:border-terracotta-500 outline-none"
              />
              <button data-testid="profile-invite-btn" disabled={inviting || atLimit}
                className="h-11 px-5 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium flex items-center gap-2 disabled:opacity-60">
                <UserPlus weight="bold" /> Invite
              </button>
            </form>
            <p className="text-xs text-stoneink-500 -mt-2">
              We&apos;ll email them a secure link they can use to join — works even if they don&apos;t have a Nestled account yet.
            </p>
            <div className="space-y-2">
              <div className="flex items-center justify-between px-4 py-3 rounded-xl border border-sand-300 bg-sand-100">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-terracotta-500 text-white grid place-items-center"><Crown weight="duotone" size={16} /></div>
                  <div>
                    <div className="text-sm font-medium text-stoneink-900">{user?.name} (you)</div>
                    <div className="text-xs text-stoneink-500">Owner · {user?.email}</div>
                  </div>
                </div>
              </div>
              {members.length === 0 ? (
                <div className="rounded-xl border border-dashed border-sand-300 bg-sand-100 p-5 text-center text-sm text-stoneink-500" data-testid="profile-no-members">
                  No family members yet — invite someone above to get started.
                </div>
              ) : members.map((m) => (
                <div key={m.id} data-testid={`profile-member-${m.id}`}
                  className="flex items-center justify-between px-4 py-3 rounded-xl border border-sand-300 bg-white">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-sage-400/20 text-sage-600 grid place-items-center"><Users weight="duotone" size={16} /></div>
                    <div>
                      <div className="text-sm font-medium text-stoneink-900">{m.name}</div>
                      <div className="text-xs text-stoneink-500">{m.email}</div>
                    </div>
                  </div>
                  <button onClick={() => removeMember(m.id)} className="p-2 rounded-lg hover:bg-sand-200 text-stoneink-500 hover:text-destructive" data-testid={`profile-remove-${m.id}`}>
                    <Trash size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
          );
        })() : isFamilyMember ? (
          <div className="rounded-2xl border border-sand-300 bg-sand-100 p-5">
            <div className="text-sm text-stoneink-700">
              You&apos;re in <span className="font-medium text-stoneink-900">{household.owner?.name}</span>&apos;s household.
            </div>
            <div className="text-xs text-stoneink-500 mt-1">{household.owner?.email}</div>
            <button onClick={leaveHousehold} data-testid="profile-leave-btn"
              className="mt-4 h-10 px-4 rounded-xl bg-white border border-sand-300 hover:border-destructive hover:text-destructive text-stoneink-700 text-sm font-medium">
              Leave household
            </button>
          </div>
        ) : (
          <Link to="/pricing" data-testid="profile-family-cta"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium">
            See Family plan →
          </Link>
        )}
      </Section>

      {/* Danger zone */}
      <Section icon={SignOut} title="Sign out" subtitle="End your session on this device." testid="section-danger">
        <button onClick={doLogout} data-testid="profile-logout-btn"
          className="h-11 px-5 rounded-xl bg-white border border-sand-300 hover:border-destructive hover:text-destructive text-stoneink-700 font-medium flex items-center gap-2">
          <SignOut size={16} /> Sign out
        </button>
      </Section>

      {/* Delete account */}
      <DeleteAccountSection />
    </div>
  );
}

function DeleteAccountSection() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const doDelete = async () => {
    if (!password) { toast.error("Enter your password to confirm."); return; }
    if (!window.confirm("This will permanently delete your account and ALL your data. There is no undo. Are you sure?")) return;
    setBusy(true);
    try {
      await api.delete("/auth/account", { data: { password } });
      toast.success("Your account has been deleted.");
      await logout().catch(() => {});
      navigate("/");
    } catch (err) {
      toast.error(formatError(err) || "Could not delete account.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      data-testid="section-delete-account"
      className="rounded-3xl border-2 border-destructive/30 bg-destructive/5 p-6 sm:p-8"
    >
      <div className="flex items-start gap-4 mb-6">
        <div className="h-11 w-11 rounded-2xl bg-destructive/10 text-destructive grid place-items-center shrink-0">
          <Trash weight="duotone" size={22} />
        </div>
        <div>
          <h2 className="font-heading text-xl sm:text-2xl text-stoneink-900 font-medium tracking-tight">Danger zone</h2>
          <p className="text-sm text-stoneink-500 mt-1">Permanently delete your account and all your data. This cannot be undone.</p>
        </div>
      </div>

      {!open ? (
        <button
          onClick={() => setOpen(true)}
          data-testid="profile-delete-account-btn"
          className="h-11 px-5 rounded-xl bg-white border border-destructive/40 text-destructive hover:bg-destructive hover:text-white font-medium flex items-center gap-2 transition-colors"
        >
          <Trash size={16} /> Delete my account
        </button>
      ) : (
        <div className="space-y-4">
          <div className="rounded-xl bg-white border border-destructive/30 p-4 text-sm text-stoneink-700">
            <p className="font-medium text-destructive mb-2">You're about to permanently delete:</p>
            <ul className="list-disc pl-5 space-y-0.5 text-stoneink-700">
              <li>Your profile, password, and all login info</li>
              <li>Every item, room, and category you've created</li>
              <li>Your household and any invite codes</li>
              <li>Any active subscription will be cancelled</li>
            </ul>
            <p className="mt-2 text-stoneink-500">For details, see <Link to="/delete-account" className="text-terracotta-600 hover:underline">our deletion policy</Link>.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-stoneink-700 mb-1.5">Confirm with your password</label>
            <input
              type="password" value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="profile-delete-confirm-password"
              placeholder="Your current password"
              className="w-full px-4 h-11 rounded-xl bg-white border border-sand-300 focus:border-destructive outline-none"
              autoComplete="current-password"
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={doDelete} disabled={busy || !password}
              data-testid="profile-delete-account-confirm-btn"
              className="h-11 px-5 rounded-xl bg-destructive hover:bg-destructive/90 text-white font-medium flex items-center gap-2 transition-colors disabled:opacity-50"
            >
              <Trash size={16} /> {busy ? "Deleting…" : "Permanently delete account"}
            </button>
            <button
              onClick={() => { setOpen(false); setPassword(""); }}
              data-testid="profile-delete-account-cancel-btn"
              className="h-11 px-5 rounded-xl bg-white border border-sand-300 text-stoneink-700 hover:bg-sand-100 font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </motion.section>
  );
}
